import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROTULO_ESTADO_SLA, type EstadoSla, type OrigemLead } from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import {
  CabecalhoDePagina,
  CabecalhoDeTabela,
  CLASSE_TABELA,
} from '../componentes/Superficie.tsx';
import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import {
  CampoCaixa,
  CampoSelecao,
  CampoTexto,
  CampoTextoLongo,
} from '../componentes/Campo.tsx';
import { useSessao } from '../autenticacao/sessao.tsx';
import { ConfirmarExclusao } from '../componentes/ConfirmarExclusao.tsx';
import { Dialogo } from '../componentes/Dialogo.tsx';
import { useOrigens } from '../dominio/origens.ts';

type Lead = {
  id: string;
  origem: OrigemLead;
  campanha: string | null;
  dataEntrada: string;
  estadoSla: EstadoSla;
  resgatadoEm: string | null;
  contato: { id: string; nome: string; telefone: string | null; email: string | null };
  responsavel: { id: string; nome: string };
};

type Duplicidade = { contatoId: string; nome: string; campos: string[] };

type DadosDoFormulario = {
  contato: {
    tipo: 'PESSOA' | 'EMPRESA';
    nome: string;
    documento?: string;
    telefone?: string;
    whatsapp?: string;
    email?: string;
    observacoes?: string;
    naoContatar: boolean;
  };
  lead: {
    origem: OrigemLead;
    campanha?: string;
    consentimento?: string;
    podeReceberContato: boolean;
    observacoes?: string;
  };
};

/**
 * Entrada comercial.
 *
 * O alerta de duplicidade não é conveniência de interface: a API recusa criar
 * uma segunda identidade sem decisão explícita, e esta tela apenas apresenta a
 * escolha.
 */
export function PaginaLeads(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [duplicidades, setDuplicidades] = useState<Duplicidade[] | null>(null);
  const [pendente, setPendente] = useState<DadosDoFormulario | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const origens = useOrigens();
  const { usuaria } = useSessao();
  // Excluir é da Administradora (NEX-223); a API recusa os outros papéis.
  const podeExcluir = usuaria?.papel === 'ADMINISTRADORA';
  const [emExclusao, setEmExclusao] = useState<Lead | null>(null);

  const consulta = useQuery({
    queryKey: ['leads'],
    queryFn: () => chamarApi<Lead[]>('/leads'),
  });

  const salvar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chamarApi<{ leadId: string }>('/leads', { metodo: 'POST', corpo }),
    onSuccess: async () => {
      setFormularioAberto(false);
      setDuplicidades(null);
      setPendente(null);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['leads'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['contatos'] });
    },
    onError: (causa: unknown) => {
      if (causa instanceof ErroDaApi && causa.status === 409) {
        setDuplicidades(extrairDuplicidades(causa));
        return;
      }
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar o lead.');
    },
  });

  function aoEnviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    const dados = lerFormulario(campos);

    setErro(null);
    setDuplicidades(null);
    setPendente(dados);
    salvar.mutate(dados as unknown as Record<string, unknown>);
  }

  function associarAoContato(contatoId: string): void {
    if (!pendente) return;
    salvar.mutate({ contatoId, lead: pendente.lead });
  }

  function registrarComoOutraIdentidade(): void {
    if (!pendente) return;
    salvar.mutate({ ...pendente, confirmarNovaIdentidade: true });
  }

  return (
    <section>
      <div className="flex items-center justify-between">
        <div>
          <CabecalhoDePagina
        area="Comercial"
        titulo="Leads"
        descricao="Toda entrada comercial nasce sob responsabilidade da Administradora."
      />
        </div>
        <button
          type="button"
          onClick={() => {
            setFormularioAberto((aberto) => !aberto);
            setDuplicidades(null);
            setErro(null);
          }}
          className="rounded-lg bg-marinho px-4 py-2 text-sm font-medium text-white transition hover:bg-marinho-claro"
        >
          Novo lead
        </button>
      </div>

      {erro ? (
        <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      {duplicidades && duplicidades.length > 0 ? (
        <div
          data-teste="alerta-duplicidade"
          className="mt-4 rounded-xl border border-alerta/40 bg-dourado-suave p-5"
        >
          <h2 className="font-semibold text-marinho">Já existe um Contato com estes dados</h2>
          <p className="mt-1 text-sm text-texto-suave">
            Reutilize a identidade para manter a jornada num só lugar, ou confirme que se trata de
            outra pessoa ou empresa.
          </p>

          <ul className="mt-4 space-y-2">
            {duplicidades.map((duplicidade) => (
              <li
                key={duplicidade.contatoId}
                className="flex items-center justify-between rounded-lg bg-superficie px-4 py-3"
              >
                <div>
                  <p className="font-medium">{duplicidade.nome}</p>
                  <p className="text-sm text-texto-suave">
                    Coincide em: {duplicidade.campos.join(', ')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => associarAoContato(duplicidade.contatoId)}
                  className="rounded-lg bg-marinho px-3 py-1.5 text-sm font-medium text-white transition hover:bg-marinho-claro"
                >
                  Associar a este Contato
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={registrarComoOutraIdentidade}
            className="mt-4 rounded-lg border border-linha bg-superficie px-3 py-1.5 text-sm transition hover:bg-marinho-suave"
          >
            É outra pessoa ou empresa
          </button>
        </div>
      ) : null}

      {formularioAberto ? <FormularioDeLead aoEnviar={aoEnviar} enviando={salvar.isPending} /> : null}

      <div className="mt-6 overflow-x-auto rounded-xl bg-superficie ring-1 ring-linha">
        <table className={CLASSE_TABELA}>
          <CabecalhoDeTabela
            colunas={[
              'Contato',
              'Origem',
              'Campanha',
              'SLA',
              'Responsável',
              ...(podeExcluir ? ['Ações'] : []),
            ]}
          />
          <tbody>
            {consulta.data?.map((lead) => (
              <tr key={lead.id} className="border-b border-linha last:border-0">
                <td className="px-3.25 py-3">
                  <p className="font-medium">{lead.contato.nome}</p>
                  <p className="text-texto-suave">{lead.contato.telefone ?? lead.contato.email}</p>
                </td>
                <td className="px-3.25 py-3">
                  {origens.nome(lead.origem)}
                  {/*
                    Junto da origem, e não na coluna de SLA: é procedência, e
                    responde por que um Lead de semanas atrás aparece hoje na
                    lista. A coluna de SLA volta a dizer "Atendido" assim que
                    alguém fala com a pessoa, e aí deixa de responder isso.
                  */}
                  {lead.resgatadoEm ? (
                    <p className="text-texto-suave">
                      Resgatado em {new Date(lead.resgatadoEm).toLocaleDateString('pt-BR')}
                    </p>
                  ) : null}
                </td>
                <td className="px-3.25 py-3">{lead.campanha ?? '—'}</td>
                <td className="px-3.25 py-3">{ROTULO_ESTADO_SLA[lead.estadoSla]}</td>
                <td className="px-3.25 py-3">{lead.responsavel.nome}</td>
                {podeExcluir ? (
                  <td className="px-3.25 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setEmExclusao(lead)}
                      aria-label={`Excluir o lead de ${lead.contato.nome}`}
                      className="rounded-lg border border-erro/40 px-2.5 py-1 text-xs font-semibold text-erro transition hover:bg-erro/5"
                    >
                      Excluir
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
        {consulta.isLoading ? <p className="p-4 text-texto-suave">Carregando…</p> : null}
        {consulta.data?.length === 0 ? (
          <p className="p-4 text-texto-suave">Nenhum lead registrado ainda.</p>
        ) : null}
      </div>

      {emExclusao ? (
        <Dialogo
          area="Comercial"
          titulo="Excluir lead"
          descricao={`${emExclusao.contato.nome} — o Lead e as vendas que nasceram dele.`}
          aoFechar={() => setEmExclusao(null)}
        >
          <ConfirmarExclusao
            rota={`/leads/${emExclusao.id}`}
            aoVoltar={() => setEmExclusao(null)}
            aoExcluir={() => setEmExclusao(null)}
          />
        </Dialogo>
      ) : null}
    </section>
  );
}

function FormularioDeLead({
  aoEnviar,
  enviando,
}: {
  aoEnviar: (evento: FormEvent<HTMLFormElement>) => void;
  enviando: boolean;
}): React.JSX.Element {
  const origens = useOrigens();

  return (
    <form onSubmit={aoEnviar} className="mt-4 rounded-xl bg-superficie p-5 ring-1 ring-linha">
      <h2 className="font-semibold text-marinho">Identidade</h2>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        <CampoSelecao rotulo="Tipo" name="tipo" defaultValue="PESSOA">
          <option value="PESSOA">Pessoa</option>
          <option value="EMPRESA">Empresa</option>
        </CampoSelecao>
        <CampoTexto rotulo="Nome ou empresa" name="nome" required className="md:col-span-2" />
        <CampoTexto rotulo="CPF ou CNPJ" name="documento" />
        <CampoTexto rotulo="Telefone" name="telefone" />
        <CampoTexto rotulo="WhatsApp" name="whatsapp" />
        <CampoTexto rotulo="E-mail" name="email" type="email" className="md:col-span-2" />
        <CampoCaixa rotulo="Não contatar" name="naoContatar" className="self-end pb-2" />
      </div>

      <h2 className="mt-6 font-semibold text-marinho">Entrada comercial</h2>
      <div className="mt-3 grid gap-4 md:grid-cols-3">
        {/* Meta Ads entra pela integração, não por cadastro manual. */}
        <CampoSelecao
          key={origens.pronta ? 'origens-prontas' : 'origens-carregando'}
          rotulo="Origem"
          name="origem"
          defaultValue="REDUCEE"
        >
          {origens.ativas
            .filter((origem) => origem.codigo !== 'META_ADS')
            .map((origem) => (
              <option key={origem.codigo} value={origem.codigo}>
                {origem.nome}
              </option>
            ))}
        </CampoSelecao>
        <CampoTexto rotulo="Campanha" name="campanha" />
        <CampoTexto rotulo="Consentimento" name="consentimento" />
        <CampoTextoLongo rotulo="Observações" name="observacoes" rows={2} className="md:col-span-3" />
      </div>

      <button
        type="submit"
        disabled={enviando}
        className="mt-5 rounded-lg bg-marinho px-4 py-2 text-sm font-medium text-white transition hover:bg-marinho-claro disabled:opacity-60"
      >
        Salvar lead
      </button>
    </form>
  );
}

function lerFormulario(campos: FormData): DadosDoFormulario {
  const texto = (chave: string): string | undefined => {
    const valor = String(campos.get(chave) ?? '').trim();
    return valor === '' ? undefined : valor;
  };

  return {
    contato: {
      tipo: (texto('tipo') ?? 'PESSOA') as 'PESSOA' | 'EMPRESA',
      nome: texto('nome') ?? '',
      documento: texto('documento'),
      telefone: texto('telefone'),
      whatsapp: texto('whatsapp'),
      email: texto('email'),
      naoContatar: campos.get('naoContatar') === 'on',
    },
    lead: {
      origem: (texto('origem') ?? 'REDUCEE') as OrigemLead,
      campanha: texto('campanha'),
      consentimento: texto('consentimento'),
      podeReceberContato: campos.get('naoContatar') !== 'on',
      observacoes: texto('observacoes'),
    },
  };
}

function extrairDuplicidades(erro: ErroDaApi): Duplicidade[] {
  const lista = erro.corpo?.['duplicidades'];
  return Array.isArray(lista) ? (lista as Duplicidade[]) : [];
}
